import { finder } from '@medv/finder';
import getXPath from 'get-xpath';
import { NextStepButtonType } from '../../features/demo/dto/FrameTooltipDTO';

const whitelistedDefaultAttributes = ['src', 'href'];

const htmlElementAttributes: Record<string, string[]> = {
  '*': [
    'accesskey',
    'autocapitalize',
    'autofocus',
    'class',
    'contenteditable',
    'dir',
    'draggable',
    'enterkeyhint',
    'hidden',
    'id',
    'inert',
    'inputmode',
    'is',
    'itemid',
    'itemprop',
    'itemref',
    'itemscope',
    'itemtype',
    'lang',
    'nonce',
    'popover',
    'slot',
    'spellcheck',
    'style',
    'tabindex',
    'title',
    'translate'
  ],
  a: [
    'charset',
    'coords',
    'download',
    'href',
    'hreflang',
    'name',
    'ping',
    'referrerpolicy',
    'rel',
    'rev',
    'shape',
    'target',
    'type'
  ],
  applet: [
    'align',
    'alt',
    'archive',
    'code',
    'codebase',
    'height',
    'hspace',
    'name',
    'object',
    'vspace',
    'width'
  ],
  area: [
    'alt',
    'coords',
    'download',
    'href',
    'hreflang',
    'nohref',
    'ping',
    'referrerpolicy',
    'rel',
    'shape',
    'target',
    'type'
  ],
  audio: ['autoplay', 'controls', 'crossorigin', 'loop', 'muted', 'preload', 'src'],
  base: ['href', 'target'],
  basefont: ['color', 'face', 'size'],
  blockquote: ['cite'],
  body: ['alink', 'background', 'bgcolor', 'link', 'text', 'vlink'],
  br: ['clear'],
  button: [
    'disabled',
    'form',
    'formaction',
    'formenctype',
    'formmethod',
    'formnovalidate',
    'formtarget',
    'name',
    'popovertarget',
    'popovertargetaction',
    'type',
    'value'
  ],
  canvas: ['height', 'width'],
  caption: ['align'],
  col: ['align', 'char', 'charoff', 'span', 'valign', 'width'],
  colgroup: ['align', 'char', 'charoff', 'span', 'valign', 'width'],
  data: ['value'],
  del: ['cite', 'datetime'],
  details: ['open'],
  dialog: ['open'],
  dir: ['compact'],
  div: ['align'],
  dl: ['compact'],
  embed: ['height', 'src', 'type', 'width'],
  fieldset: ['disabled', 'form', 'name'],
  font: ['color', 'face', 'size'],
  form: [
    'accept',
    'accept-charset',
    'action',
    'autocomplete',
    'enctype',
    'method',
    'name',
    'novalidate',
    'target'
  ],
  frame: [
    'frameborder',
    'longdesc',
    'marginheight',
    'marginwidth',
    'name',
    'noresize',
    'scrolling',
    'src'
  ],
  frameset: ['cols', 'rows'],
  h1: ['align'],
  h2: ['align'],
  h3: ['align'],
  h4: ['align'],
  h5: ['align'],
  h6: ['align'],
  head: ['profile'],
  hr: ['align', 'noshade', 'size', 'width'],
  html: ['manifest', 'version'],
  iframe: [
    'align',
    'allow',
    'allowfullscreen',
    'allowpaymentrequest',
    'allowusermedia',
    'frameborder',
    'height',
    'loading',
    'longdesc',
    'marginheight',
    'marginwidth',
    'name',
    'referrerpolicy',
    'sandbox',
    'scrolling',
    'src',
    'srcdoc',
    'width'
  ],
  img: [
    'align',
    'alt',
    'border',
    'crossorigin',
    'decoding',
    'fetchpriority',
    'height',
    'hspace',
    'ismap',
    'loading',
    'longdesc',
    'name',
    'referrerpolicy',
    'sizes',
    'src',
    'srcset',
    'usemap',
    'vspace',
    'width'
  ],
  input: [
    'accept',
    'align',
    'alt',
    'autocomplete',
    'checked',
    'dirname',
    'disabled',
    'form',
    'formaction',
    'formenctype',
    'formmethod',
    'formnovalidate',
    'formtarget',
    'height',
    'ismap',
    'list',
    'max',
    'maxlength',
    'min',
    'minlength',
    'multiple',
    'name',
    'pattern',
    'placeholder',
    'popovertarget',
    'popovertargetaction',
    'readonly',
    'required',
    'size',
    'src',
    'step',
    'type',
    'usemap',
    'value',
    'width'
  ],
  ins: ['cite', 'datetime'],
  isindex: ['prompt'],
  label: ['for', 'form'],
  legend: ['align'],
  li: ['type', 'value'],
  link: [
    'as',
    'blocking',
    'charset',
    'color',
    'crossorigin',
    'disabled',
    'fetchpriority',
    'href',
    'hreflang',
    'imagesizes',
    'imagesrcset',
    'integrity',
    'media',
    'referrerpolicy',
    'rel',
    'rev',
    'sizes',
    'target',
    'type'
  ],
  map: ['name'],
  menu: ['compact'],
  meta: ['charset', 'content', 'http-equiv', 'media', 'name', 'scheme'],
  meter: ['high', 'low', 'max', 'min', 'optimum', 'value'],
  object: [
    'align',
    'archive',
    'border',
    'classid',
    'codebase',
    'codetype',
    'data',
    'declare',
    'form',
    'height',
    'hspace',
    'name',
    'standby',
    'type',
    'typemustmatch',
    'usemap',
    'vspace',
    'width'
  ],
  ol: ['compact', 'reversed', 'start', 'type'],
  optgroup: ['disabled', 'label'],
  option: ['disabled', 'label', 'selected', 'value'],
  output: ['for', 'form', 'name'],
  p: ['align'],
  param: ['name', 'type', 'value', 'valuetype'],
  pre: ['width'],
  progress: ['max', 'value'],
  q: ['cite'],
  script: [
    'async',
    'blocking',
    'charset',
    'crossorigin',
    'defer',
    'fetchpriority',
    'integrity',
    'language',
    'nomodule',
    'referrerpolicy',
    'src',
    'type'
  ],
  select: ['autocomplete', 'disabled', 'form', 'multiple', 'name', 'required', 'size'],
  slot: ['name'],
  source: ['height', 'media', 'sizes', 'src', 'srcset', 'type', 'width'],
  style: ['blocking', 'media', 'type'],
  table: [
    'align',
    'bgcolor',
    'border',
    'cellpadding',
    'cellspacing',
    'frame',
    'rules',
    'summary',
    'width'
  ],
  tbody: ['align', 'char', 'charoff', 'valign'],
  td: [
    'abbr',
    'align',
    'axis',
    'bgcolor',
    'char',
    'charoff',
    'colspan',
    'headers',
    'height',
    'nowrap',
    'rowspan',
    'scope',
    'valign',
    'width'
  ],
  textarea: [
    'autocomplete',
    'cols',
    'dirname',
    'disabled',
    'form',
    'maxlength',
    'minlength',
    'name',
    'placeholder',
    'readonly',
    'required',
    'rows',
    'wrap'
  ],
  tfoot: ['align', 'char', 'charoff', 'valign'],
  th: [
    'abbr',
    'align',
    'axis',
    'bgcolor',
    'char',
    'charoff',
    'colspan',
    'headers',
    'height',
    'nowrap',
    'rowspan',
    'scope',
    'valign',
    'width'
  ],
  thead: ['align', 'char', 'charoff', 'valign'],
  time: ['datetime'],
  tr: ['align', 'bgcolor', 'char', 'charoff', 'valign'],
  track: ['default', 'kind', 'label', 'src', 'srclang'],
  ul: ['compact', 'type'],
  video: [
    'autoplay',
    'controls',
    'crossorigin',
    'height',
    'loop',
    'muted',
    'playsinline',
    'poster',
    'preload',
    'src',
    'width'
  ]
};

function isHTMLElement(node: Node | null) {
  return node?.nodeType === 1;
}

function findNthChildSelector(element: HTMLElement) {
  const siblings = Array.from(element.parentElement?.children ?? []);
  let selector = element.tagName.toLowerCase();
  if (siblings.length > 1) {
    const index = siblings.indexOf(element);
    selector += `:nth-child(${index + 1})`;
  }
  return selector;
}

function isAttributeAllowed(attributeName: string, tagName: string) {
  if (whitelistedDefaultAttributes.includes(attributeName)) {
    return true;
  }
  const globalAttrs = htmlElementAttributes['*'] ?? [];
  const tagAttrs = htmlElementAttributes[tagName.toLowerCase()] ?? [];
  const isStandard = globalAttrs.includes(attributeName) || tagAttrs.includes(attributeName);

  return !isStandard;
}

function isValidAttributeValue(value: string | null) {
  if (value == null || value.length === 0) {
    return false;
  }
  const disallowed = ['null', 'undefined', 'false', 'true'];
  const hasThreeDigits = /\d{3}/.test(value);
  return !disallowed.includes(value) && !hasThreeDigits;
}

export function findCSSPaths(
  { element, root }: { element: HTMLElement; root?: Element },
  childSegment = '',
  accumulator: string[] = [],
  depth = 0
): string[] {
  const MAX_DEPTH = 5;

  let selector = finder(element, {
    root: root ?? (element.ownerDocument as unknown as Element),
    seedMinLength: 1,
    threshold: 1000,
    className: (name: string) => {
      return (
        !name.includes('get-demo') && !name.includes('single-file') && !name.includes('getdemo-')
      );
    },
    attr(attrName: string, attrValue: string) {
      return (
        !attrName.includes('get-demo') &&
        !attrName.includes('single-file') &&
        !attrName.includes('getdemo-') &&
        !!element?.tagName &&
        isAttributeAllowed(attrName, element.tagName) &&
        isValidAttributeValue(attrValue)
      );
    }
  });

  if (childSegment && selector) {
    selector = `${selector} > ${childSegment}`;
  }

  if (isHTMLElement(element?.parentElement) && depth < MAX_DEPTH) {
    if (
      selector &&
      (isHTMLElement(element.parentElement?.parentElement ?? null) || isInShadow(element))
    ) {
      let nthChild = findNthChildSelector(element);
      if (childSegment) {
        nthChild = `${nthChild} > ${childSegment}`;
      }
      return findCSSPaths(
        { element: element.parentElement as HTMLElement, root },
        nthChild,
        accumulator.concat(selector),
        depth + 1
      );
    }
  }

  return accumulator;
}

const isInShadow = (node: ParentNode | null) => {
  for (; node; node = node.parentNode) {
    if (node.toString() === '[object ShadowRoot]') {
      return true;
    }
  }
  return false;
};

const findTextXPath = (element: Element): string | null => {
  try {
    const inner = element.innerHTML;

    if (!inner.includes('<') && inner.length) {
      return `//${element.tagName}[text()="${inner}"]`;
    }

    return null;
  } catch (error) {
    return null;
  }
};

const findFullXPath = (element: Element): string => {
  return getXPath(element);
};

const getPaths = (
  element: HTMLElement
): { xpath: string; cssPath: string[]; textXPath: string | null; shadowRoot?: boolean }[] => {
  const paths = [];
  let currentElement: HTMLElement = element;

  while (currentElement) {
    if (currentElement && isInShadow(currentElement)) {
      const host = ((currentElement as HTMLElement).getRootNode() as ShadowRoot)
        .host as HTMLElement;
      paths.unshift({
        xpath: findFullXPath(currentElement as HTMLElement),
        textXPath: findTextXPath(currentElement as HTMLElement),
        cssPath: findCSSPaths({
          element: currentElement as HTMLElement,
          root: currentElement.getRootNode() as unknown as Element
        }),
        shadowRoot: true
      });
      currentElement = host;
    } else if (
      currentElement &&
      currentElement.ownerDocument.defaultView?.frameElement &&
      !['get-demo-player', 'get-demo-secondary-player'].includes(
        currentElement.ownerDocument.defaultView?.frameElement.id
      )
    ) {
      paths.unshift({
        xpath: findFullXPath(currentElement as HTMLElement),
        textXPath: findTextXPath(currentElement as HTMLElement),
        cssPath: findCSSPaths({
          element: currentElement as HTMLElement,
          root: currentElement.ownerDocument as unknown as Element
        }),
        iframe: true
      });
      currentElement = currentElement.ownerDocument.defaultView?.frameElement as HTMLElement;
    } else {
      paths.unshift({
        xpath: findFullXPath(currentElement as HTMLElement),
        cssPath: findCSSPaths({
          element: currentElement as HTMLElement
        }),
        textXPath: findTextXPath(currentElement as HTMLElement)
      });
      return paths;
    }
  }
  return paths;
};

function findElementByPaths(
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
  }[]
): HTMLElement | null {
  let currentDocument: Document | ShadowRoot = document;
  let currentElement: HTMLElement | null = null;

  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    const isLastPath = i === paths.length - 1;

    const tryFindElement = (): HTMLElement | null => {
      if (path.cssPath && path.cssPath.length > 0) {
        for (const cssPath of path.cssPath) {
          try {
            const element = currentDocument.querySelector(cssPath) as HTMLElement;
            if (element) {
              return element;
            }
          } catch (error) {
            console.warn('Error in findElementByPaths', error);
          }
        }
      }

      if (path.textXPath && currentDocument instanceof Document) {
        try {
          const result = currentDocument.evaluate(
            path.textXPath,
            currentDocument,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
          );
          const element = result.singleNodeValue as HTMLElement;
          if (element) {
            return element;
          }
        } catch (error) {
          console.warn('Error in findElementByPaths', error);
        }
      }

      if (currentDocument instanceof Document) {
        try {
          const result = currentDocument.evaluate(
            path.xpath,
            currentDocument,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
          );
          const element = result.singleNodeValue as HTMLElement;
          if (element) {
            return element;
          }
        } catch (error) {
          console.warn('Error in findElementByPaths', error);
        }
      }

      return null;
    };

    currentElement = tryFindElement();

    if (!currentElement) {
      return null;
    }

    if (!isLastPath && paths[i + 1]?.shadowRoot) {
      const shadowRoot = currentElement.shadowRoot;
      if (!shadowRoot) {
        return null;
      }
      currentDocument = shadowRoot;
    }
  }

  return currentElement;
}

const splitPathsByIframe = (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[]
): {
  mainPaths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[];
  framePaths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
    iframe?: boolean;
  }[];
  frameElement?: HTMLIFrameElement | null;
} => {
  const iframeIndex = paths.findIndex(path => path.iframe === true);

  if (iframeIndex === -1) {
    return {
      mainPaths: [...paths],
      framePaths: []
    };
  } else {
    const mainPaths = paths.slice(0, iframeIndex);
    const framePaths = paths.slice(iframeIndex);
    const frameElement = findElementByPaths(mainPaths) as HTMLIFrameElement;

    const firstFramePath = framePaths.slice(0, 1);
    const restFramePaths = framePaths.slice(1);
    return {
      mainPaths,
      framePaths: [{ ...firstFramePath[0], iframe: false }, ...restFramePaths],
      frameElement
    };
  }
};

const isElementVisible = (element?: HTMLElement): boolean => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return false;
  }

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }

  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    const parentStyle = window.getComputedStyle(parent);

    if (parentStyle.display === 'contents') {
      parent = parent.parentElement;
      continue;
    }

    if (
      parentStyle.overflow === 'hidden' ||
      parentStyle.overflowX === 'hidden' ||
      parentStyle.overflowY === 'hidden'
    ) {
      const elementRect = element.getBoundingClientRect();
      const parentOffset = parent.getBoundingClientRect();

      if (
        elementRect.left >= parentOffset.right ||
        elementRect.right <= parentOffset.left ||
        elementRect.top >= parentOffset.bottom ||
        elementRect.bottom <= parentOffset.top
      ) {
        return false;
      }
    }

    parent = parent.parentElement;
  }

  if (isElementFullyCoveredWithShadow(element)) {
    return false;
  }

  return true;
};

const buildVisibilityChain = (
  target: Element
): Array<{ root: Document | ShadowRoot; node: Element }> => {
  const chain: Array<{ root: Document | ShadowRoot; node: Element }> = [];
  let node: Element = target;
  let root: Document | ShadowRoot = node.getRootNode() as Document | ShadowRoot;

  while (true as any) {
    chain.unshift({ root, node });
    if (root instanceof ShadowRoot) {
      node = root.host;
      root = node.getRootNode() as Document | ShadowRoot;
    } else {
      break;
    }
  }

  return chain;
};

const pointIsVisible = (
  chain: Array<{ root: Document | ShadowRoot; node: Element }>,
  x: number,
  y: number
): boolean => {
  for (const { root, node } of chain) {
    let topElem: Element | null = null;

    if (typeof (root as any).elementsFromPoint === 'function') {
      const elems = (root as any).elementsFromPoint(x, y) as Element[];
      if (!elems || elems.length === 0) return false;
      topElem = elems[0];
    } else if (typeof (root as any).elementFromPoint === 'function') {
      topElem = (root as any).elementFromPoint(x, y) as Element | null;
      if (!topElem) return false;
    } else {
      return false;
    }

    if (topElem !== node && !node.contains(topElem)) {
      return false;
    }
  }

  return true;
};

const isElementFullyCoveredWithShadow = (element: Element, samplesX = 5, samplesY = 5): boolean => {
  const rects = Array.from(element.getClientRects());
  if (!rects.length) return true;

  const chain = buildVisibilityChain(element);

  for (const rect of rects) {
    const stepX = rect.width / samplesX;
    const stepY = rect.height / samplesY;
    if (stepX <= 0 || stepY <= 0) continue;

    for (let i = 0; i < samplesX; i++) {
      const x = rect.left + stepX * (i + 0.5);
      for (let j = 0; j < samplesY; j++) {
        const y = rect.top + stepY * (j + 0.5);
        if (pointIsVisible(chain, x, y)) {
          return false;
        }
      }
    }
  }

  return true;
};

const isElementOutOfViewport = (dimensions: {
  x: number;
  y: number;
  width: number;
  height: number;
}): boolean => {
  const rect = {
    left: dimensions.x,
    top: dimensions.y,
    right: dimensions.x + dimensions.width,
    bottom: dimensions.y + dimensions.height
  };
  return (
    rect.bottom < 0 ||
    rect.top > window.innerHeight ||
    rect.right < 0 ||
    rect.left > window.innerWidth
  );
};

interface ElementDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
  containerWidth: number;
  containerHeight: number;
  element?: HTMLElement;
  isOutOfViewport?: boolean;
  frameElement?: HTMLIFrameElement;
  isElementVisible?: boolean;
}

interface ElementDimensionsOptions {
  onElementFound?: (element: HTMLElement) => void;
  onElementLost?: () => void;
  onBeforeFind?: () => void;
  isOutOfViewport?: boolean;
}

const getElementDimensions = (element?: HTMLElement): ElementDimensions | null => {
  if (!element || !element.isConnected) return null;
  const { x, y, width, height } = element?.getBoundingClientRect() || {
    x: 0,
    y: 0,
    width: 0,
    height: 0
  };

  const isOutOfViewport = isElementOutOfViewport({ x, y, width, height });
  const isElementVisibleValue = isElementVisible(element);

  return {
    x,
    y,
    width,
    height,
    containerWidth: window.innerWidth,
    containerHeight: window.innerHeight,
    element,
    isOutOfViewport,
    isElementVisible: isElementVisibleValue
  };
};

const waitForElement = (
  paths: { xpath: string; cssPath: string[]; textXPath: string | null; shadowRoot?: boolean }[],
  options: ElementDimensionsOptions = {}
): Promise<HTMLElement | null> => {
  return new Promise(async resolve => {
    options.onBeforeFind?.();
    const element = findElementByPaths(paths);

    if (element) {
      options.onElementFound?.(element);
      resolve(element);
      return;
    } else {
      options.onElementLost?.();
    }

    const observer = new MutationObserver((_, obs) => {
      const element = findElementByPaths(paths);
      if (element) {
        obs.disconnect();
        options.onElementFound?.(element);
        resolve(element);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
  });
};

const getElementDimensionsByPath = async (
  paths: { xpath: string; cssPath: string[]; textXPath: string | null; shadowRoot?: boolean }[],
  options: ElementDimensionsOptions = {}
): Promise<ElementDimensions | null> => {
  const element = await waitForElement(paths, options);
  return getElementDimensions(element as HTMLElement);
};

const getOverallElementDimensionsByPath = async (
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
  }[],
  options: ElementDimensionsOptions = {},
  nextStepButtonType: NextStepButtonType | null = null
) => {
  const { mainPaths, framePaths, frameElement } = splitPathsByIframe(paths);

  let dimensions = (await getElementDimensionsByPath(mainPaths, options)) as ElementDimensions;
  if (frameElement) {
    const frameDimensions = (await new Promise(resolve => {
      frameElement.contentWindow?.postMessage(
        {
          type: 'GET_IFRAME_ELEMENT_DIMENSIONS',
          paths: framePaths,
          nextStepButtonType
        },
        '*'
      );
      const messageHandler = async (event: MessageEvent) => {
        if (event.data.type === 'SET_IFRAME_ELEMENT_DIMENSIONS') {
          window.removeEventListener('message', messageHandler);
          resolve(event.data.dimensions);
        }
      };
      window.addEventListener('message', messageHandler);
    })) as any;
    dimensions = {
      x: dimensions.x + frameDimensions.x,
      y: dimensions.y + frameDimensions.y,
      width: frameDimensions.width,
      height: frameDimensions.height,
      containerWidth: dimensions.containerWidth,
      containerHeight: dimensions.containerHeight,
      element: dimensions.element,
      frameElement: frameElement as HTMLIFrameElement | undefined,
      isElementVisible: frameDimensions.isElementVisible && dimensions.isElementVisible
    };
  }
  dimensions.isOutOfViewport = isElementOutOfViewport(dimensions);
  return dimensions;
};

const getOverallElementDimensionsByElement = async (
  element: HTMLElement,
  paths: {
    xpath: string;
    cssPath: string[];
    textXPath: string | null;
    shadowRoot?: boolean;
  }[]
) => {
  if (!element) return;
  const { framePaths, frameElement } = splitPathsByIframe(paths);

  let dimensions = getElementDimensions(element);
  if (!dimensions) return null;
  if (frameElement) {
    const frameDimensions = (await new Promise(resolve => {
      frameElement.contentWindow?.postMessage(
        {
          type: 'GET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT',
          paths: framePaths
        },
        '*'
      );
      const messageHandler = async (event: MessageEvent) => {
        if (event.data.type === 'SET_IFRAME_ELEMENT_DIMENSIONS_BY_ELEMENT') {
          window.removeEventListener('message', messageHandler);
          resolve(event.data.dimensions);
        }
      };
      window.addEventListener('message', messageHandler);
    })) as any;
    if (!frameDimensions) return null;
    dimensions = {
      x: dimensions.x + frameDimensions.x,
      y: dimensions.y + frameDimensions.y,
      width: frameDimensions.width,
      height: frameDimensions.height,
      containerWidth: dimensions.containerWidth,
      containerHeight: dimensions.containerHeight,
      isOutOfViewport:
        isElementOutOfViewport({
          x: dimensions.x + frameDimensions.x,
          y: dimensions.y + frameDimensions.y,
          width: frameDimensions.width,
          height: frameDimensions.height
        }) ||
        dimensions.isOutOfViewport ||
        frameDimensions.isOutOfViewport,
      isElementVisible: frameDimensions.isElementVisible && dimensions.isElementVisible
    };
  }

  return dimensions;
};

const Finder = {
  getPaths,
  findElementByPaths,
  isElementVisible,
  isElementFullyCoveredWithShadow,
  isElementOutOfViewport,
  getElementDimensions,
  getElementDimensionsByPath,
  waitForElement,
  splitPathsByIframe,
  getOverallElementDimensionsByPath,
  getOverallElementDimensionsByElement,
  isInShadow
};

export default Finder;
